import { type ApexOptions } from 'apexcharts'
import dayjs from 'dayjs'

import humanizeLargeNumber from './humanizeLargeNumber'
import numberUtils from './numberUtils'

export const movingAvgSparkline: ApexOptions = {
  colors: ['var(--color_primary_500)'],
  grid: {
    padding: {
      left: 5,
      right: 5,
      top: 5,
      bottom: 5,
    },
  },
  chart: {
    animations: {
      enabled: false,
    },
    sparkline: {
      enabled: true,
    },
  },
  yaxis: {
    forceNiceScale: true,
    floating: true,
    axisTicks: {
      show: false,
    },
    axisBorder: {
      show: false,
    },
    labels: {
      show: false,
    },
  },
  tooltip: {
    fixed: {
      enabled: true,
      offsetY: 27,
    },
    marker: {
      show: false,
    },
    custom: function ({ series, seriesIndex, dataPointIndex, w }) {
      return `<div class="tooltip-relevance-index">
          <span>
            Moving avg of published videos in ${dayjs(w.globals.categoryLabels[dataPointIndex]).format('MMM D')}: 
            ${numberUtils.shortenWithoutZero(series[seriesIndex][dataPointIndex])}
          </span>
        </div>`
    },
  },
  markers: {
    size: 0,
  },
  stroke: {
    width: 2,
  },
}

export const scatterChartOptions: ApexOptions = {
  chart: {
    id: `winnin-apexchart-${Math.random()}`,
    fontFamily: 'Muli, Arial, sans-serif',
    toolbar: {
      show: true,
      tools: {
        download: false,
        pan: false,
        reset: false,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
      },
    },
    animations: {
      enabled: false,
    },
    events: {
      mounted: chart => {
        chart.windowResizeHandler()
      },
    },
    zoom: {
      enabled: false,
    },
  },
  grid: {
    borderColor: 'var(--color_neutral_200)',
  },
  markers: {
    size: 10,
  },
  xaxis: {
    tickAmount: 5,
    min: 0,
    labels: {
      formatter: function (val) {
        return numberUtils.format(val)
      },
    },
  },
  yaxis: {
    tickAmount: 5,
    labels: {
      formatter: function (val) {
        return numberUtils.format(val)
      },
    },
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    offsetY: 4,
  },
}

export const getOptionsBasedCustomLegend = ({ options, enableCustomLegends }): ApexOptions => {
  if (!enableCustomLegends) {
    return options
  }

  return {
    ...options,
    legend: {
      ...options.legend,
      show: false,
    },
  }
}

const getChartFontSize = chartData => {
  if (chartData?.[0]?.data?.length > 22) {
    return '8px'
  }
  if (chartData?.[0]?.data?.length > 12) {
    return '10px'
  }
  return '14px'
}

export const getStackedChartOptions = (chartData, selectedMetric) => {
  return {
    xaxis: {
      tickAmount: 'dataPoints',
      type: 'numeric',
      labels: {
        offsetX: 2,
        rotate: 0,
      },
    },
    dataLabels: {
      style: {
        fontSize: getChartFontSize(chartData),
      },
      dropShadow: {
        enabled: true,
      },
    },
    chart: {
      type: 'bar',
    },
    yaxis: {
      labels: {
        formatter: function (val: number, chartData: Record<string, any>) {
          const isAxisValue = chartData?.dataPointIndex === -1 || typeof chartData === 'number'

          if (isAxisValue) {
            return `${val}%`
          }

          return `${humanizeLargeNumber(val, 2)} ${selectedMetric}`
        },
      },
    },
  }
}
